/**
 * Thin calendar-flavored wrapper around the shared AttachIntegrationTemplate —
 * see that file and assets/handoffs/design_handoff_attach_integration.
 * Kept as its own route (not folded into CalendarPage.js, which is already
 * heavy and doesn't need to know about the "no integration" case).
 */
import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import AttachIntegrationTemplate from "./../templates/AttachIntegrationTemplate/AttachIntegrationTemplate";
import MajorToast from "../component/Toast/MajorToast";
import { store } from "../store";
import { calendarSettingsValidator } from "../tools";
import { useCalendarStore } from "../../../hooks/redux/calendar";
import { useIntegrationsStore } from "../../../hooks/redux/integrations";

const NAVIGATE_DELAY_MS = 900;

const AttachIntegrationPage = (props) => {
  const { calendar, updateCalendar, setCalendar, fetchCalendar, deleteCalendar } = useCalendarStore();
  const { integrations } = useIntegrationsStore();
  const history = useHistory();
  const uuid = props.match.params.uuid;

  const [calendarModel, setCalendarModel] = useState({
    uuid: "",
    name: "",
    available: 0,
    integration_uuid: null,
    template: "",
    show_private_events: 0,
    featured_events_limit: 0,
  });
  const [errors, setErrors] = useState({});
  const [attached, setAttached] = useState(false);

  useEffect(() => {
    fetchCalendar(uuid);

    return () => setCalendar(null);
  }, [uuid]);

  useEffect(() => {
    if (calendar.fetchSuccess && calendar.model) {
      setCalendarModel({
        uuid: calendar.model.uuid,
        name: calendar.model.name,
        available: calendar.model.available,
        integration_uuid: null,
        template: calendar.model.template,
        show_private_events: calendar.model.show_private_events,
        featured_events_limit: calendar.model.featured_events_limit,
      });
    }
  }, [calendar.fetchSuccess]);

  const onUpdateField = useCallback((fieldName, value) => {
    setCalendarModel((previous) => {
      const updated = { ...previous, [fieldName]: value };
      setErrors(calendarSettingsValidator(updated));
      return updated;
    });
  }, []);

  const onAttach = useCallback(async () => {
    await updateCalendar(calendarModel.uuid, calendarModel);

    if (store.getState().calendar.updateSuccess) {
      setAttached(true);
      setTimeout(() => history.push("/calendar/" + calendarModel.uuid), NAVIGATE_DELAY_MS);
    }
  }, [calendarModel, updateCalendar, history]);

  const onDelete = useCallback(
    async (e) => {
      e.preventDefault();

      if (calendar.fetch || calendar.delete) {
        return;
      }

      await deleteCalendar(uuid);

      if (store.getState().calendar.deleteSuccess) {
        history.push("/calendars");
      }
    },
    [calendar.fetch, calendar.delete, deleteCalendar, uuid, history]
  );

  return (
    <>
      <AttachIntegrationTemplate
        entityNoun="calendar"
        indexHref="/calendars"
        indexLabel="Calendars"
        widgetModel={calendarModel}
        untitled={!!calendar.model && !calendar.model.name}
        published={Boolean(calendar.model?.available)}
        onUpdateField={onUpdateField}
        formErrors={errors}
        integrations={integrations.collection}
        attaching={calendar.update}
        attachError={calendar.updateError}
        onAttach={onAttach}
        onDelete={onDelete}
      />
      {attached && <MajorToast title="Integration attached" onDismiss={() => setAttached(false)} />}
    </>
  );
};

export default AttachIntegrationPage;
